import { COMPANY } from "@byfluent/domain";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { api } from "../lib/api";
import { useMe } from "../lib/auth";
import { Icon } from "./Icons";

export interface NavItem {
  to: string;
  label: string;
  icon: () => ReactNode;
  end?: boolean;
}

/** Painéis com menu lateral no desktop e barra inferior no celular. */
export function PanelLayout({ area, items }: { area: string; items: NavItem[] }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = async () => {
    await api.post("/v1/auth/logout").catch(() => undefined);
    qc.clear();
    navigate("/entrar");
  };
  const switcher: NavItem[] = [];
  if (me?.roles.includes("admin")) {
    if (area !== "Admin") switcher.push({ to: "/admin", label: "Admin", icon: Icon.dashboard });
    if (area !== "Vendas") switcher.push({ to: "/vendas", label: "Vendas", icon: Icon.leads });
    if (area !== "Professor")
      switcher.push({ to: "/professor", label: "Professor", icon: Icon.students });
  }
  return (
    <div className="panel">
      <aside className="sidebar">
        <div className="logo">
          <img src="/logo.webp" alt="" width="36" height="36" />
          <span>
            ByFluent
            <small>{area}</small>
          </span>
        </div>
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
          >
            {i.icon()}
            {i.label}
          </NavLink>
        ))}
        {switcher.length ? (
          <div className="small" style={{ color: "#8f9bb8", padding: "16px 12px 4px" }}>
            Outras áreas
          </div>
        ) : null}
        {switcher.map((i) => (
          <NavLink key={i.to} to={i.to} className="nav-link">
            {i.icon()}
            {i.label}
          </NavLink>
        ))}
        <div className="spacer" />
        <div className="who">{me?.name}</div>
        <button
          type="button"
          className="nav-link"
          style={{ background: "none", border: 0, font: "inherit", cursor: "pointer" }}
          onClick={logout}
        >
          {Icon.logout()}
          Sair
        </button>
      </aside>
      <main className="main">
        <Outlet />
      </main>
      <nav className="bottombar" aria-label="Menu">
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
          >
            {i.icon()}
            {i.label}
          </NavLink>
        ))}
        <button
          type="button"
          className="nav-link"
          style={{ background: "none", border: 0, font: "inherit", cursor: "pointer" }}
          onClick={logout}
        >
          {Icon.logout()}
          Sair
        </button>
      </nav>
    </div>
  );
}

export function PublicLayout({
  children,
  support,
}: {
  children: ReactNode;
  support?: string | null;
}) {
  return (
    <div className="public">
      <header className="public-header">
        <div className="inner">
          <img src="/logo.webp" alt="" width="32" height="32" />
          ByFluent
        </div>
      </header>
      <main className="public-body">{children}</main>
      <footer className="public-footer">
        ByFluent, aulas particulares de inglês com professor e prática por voz com IA.
        {support ? (
          <>
            <br />
            Precisa de ajuda?{" "}
            <a href={`https://wa.me/${support}`} target="_blank" rel="noreferrer">
              WhatsApp do suporte
            </a>
          </>
        ) : null}
        <br />
        {COMPANY.brand} é uma marca de {COMPANY.legalName} · CNPJ {COMPANY.cnpj}
      </footer>
    </div>
  );
}
